import { formatCategoryName } from '@/utilities/contentHelpers'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styles from './SearchForm.module.scss'

function SearchForm({ categories, onSearch, initialSearchTerm = '', initialCategory = '' }) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const [selectedOption, setSelectedOption] = useState(initialCategory)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const listboxId = 'category-listbox'

  // Sync with URL params when they change using useEffect
  useEffect(() => {
    if (searchTerm !== initialSearchTerm) {
      setSearchTerm(initialSearchTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchTerm])

  useEffect(() => {
    if (selectedOption !== initialCategory) {
      setSelectedOption(initialCategory)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory])

  // Handle clicks outside of dropdown
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen])

  const closeDropdown = () => {
    setIsOpen(false)
    containerRef.current?.focus()
  }

  // Event handlers
  const handleCategorySelect = (categoryName, e) => {
    e.stopPropagation()
    const formattedCategory = formatCategoryName(categoryName)
    const isViewAll = formattedCategory.toLowerCase() === 'view all'
    setSelectedOption(formattedCategory)
    onSearch(searchTerm, isViewAll ? '' : formattedCategory)
    closeDropdown()
  }

  const handleKeyDown = (e, category = null) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        if (category) {
          handleCategorySelect(category, e)
        } else {
          e.stopPropagation()
          setIsOpen((prev) => !prev)
        }
        break
      case 'Escape':
        if (isOpen) closeDropdown()
        break
    }
  }
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = () => {
      setSearchTerm('')
      setSelectedOption('')
      onSearch('', '')
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.events, onSearch])

  const filteredCategories = categories.nodes.filter((category) => category.name.toLowerCase() !== 'uncategorized')
  const viewAllOption = { name: 'View All', slug: '' }
  const fullCategoryList = [viewAllOption, ...filteredCategories]

  return (
    <form onSubmit={(e) => e.preventDefault()} className={styles.searchForm} data-aos="fade-up">
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          onSearch(e.target.value, selectedOption)
        }}
        aria-label="Search Blog Posts"
      />

      <div className={styles.customSelectContainer} ref={containerRef}>
        <div
          className={styles.customSelectDropdown}
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-owns={listboxId}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen((prev) => !prev)
          }}
          onKeyDown={handleKeyDown}
        >
          <div className={styles.selectedOption}>
            {selectedOption === '' ? <span className={styles.placeholder}>Select a category</span> : <span>{selectedOption}</span>}
          </div>

          {isOpen && (
            <ul id={listboxId} className={styles.customOptions} role="listbox" aria-label="Categories">
              {fullCategoryList.map((category, index) => (
                <li
                  key={index}
                  className={styles.customOption}
                  role="option"
                  aria-selected={selectedOption.toLowerCase() === formatCategoryName(category.name).toLowerCase()}
                  tabIndex={0}
                  onClick={(e) => handleCategorySelect(category.name, e)}
                  onKeyDown={(e) => handleKeyDown(e, category.name)}
                >
                  {formatCategoryName(category.name)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </form>
  )
}

export default SearchForm
